import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { getEnrolleeOrderList } from '../common/enrollmentUtils';
import { enrollmentSchema } from '../common/premiumHistoryFormConfig';
import { IEnrollment, IEnrollmentContextProps } from '../common/types';

export const EnrollmentContext = createContext<IEnrollmentContextProps>({
    selectedEnrollment: {} as IEnrollment,
    enrollments: [],
    setEnrollments: () => {},
    disableContinue: false,
    isMrcClicked: false,
    isEdit: false,
    disableMRCEdit: false,
    enableContinueButton: () => {},
    disableContinueButton: () => {},
    enrollmentPremiumFormContext: () => {},
    setIsEdit: () => {},
    viewAddOrChangeTxn: false,
    setViewAddOrChangeTxn: () => {},
});

export const MemberMyEnrollmentProvider = ({ children }) => {
    const intl = useIntl();
    const [enrollments, setEnrollments] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [disableMRCEdit, setDisableMRCEdit] = useState(false);
    const [disableContinue, setDisableContinue] = useState(false);
    const [isMrcClicked, setisMrcClicked] = useState(false);
    const [viewAddOrChangeTxn, setViewAddOrChangeTxn] = useState(false);
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { selectedEnrollment } = useSelector((state: { memberEnrollment: any }) => state.memberEnrollment);

    const getDefaultValues = useMemo(() => {
        let enrollment = {};
        if (!!selectedEnrollment) {
            const enroleeDataDtoList = getEnrolleeOrderList(selectedEnrollment?.enrolleeDataDtoList);
            enrollment = {
                enrollmentId: selectedEnrollment?.enrollmentId,
                householdId: selectedEnrollment?.houseHoldCaseId,
                enrollmentBenefitEffectiveStartDateUI: selectedEnrollment.enrollmentBenefitEffectiveStartDateUI,
                enrollmentBenefitEffectiveEndDateUI: selectedEnrollment.enrollmentBenefitEffectiveEndDateUI,
                enrolleeDataDtoList: enroleeDataDtoList,
                enrollmentPremiumDtoList: selectedEnrollment.enrollmentPremiumDtoList,
                enrollmentBenefitEffectiveStartMonth: selectedEnrollment.enrollmentBenefitEffectiveStartMonth,
                enrollmentBenefitEffectiveEndMonth: selectedEnrollment.enrollmentBenefitEffectiveEndMonth,
            };
        }

        return enrollment;
    }, [selectedEnrollment]);

    const methods = useForm<IEnrollment>({
        defaultValues: getDefaultValues,
        resolver: zodResolver(enrollmentSchema(selectedEnrollment, userPermissions)),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        methods.reset(getDefaultValues);
    }, [selectedEnrollment]);

    const enableContinueButton = () => {
        setDisableContinue(true);
        setDisableMRCEdit(true);
    };
    const disableContinueButton = () => {
        setDisableContinue(false);
        setDisableMRCEdit(false);
    };

    const value = {
        selectedEnrollment,
        disableContinue,
        isMrcClicked,
        enrollments,
        isEdit,
        disableMRCEdit,
        viewAddOrChangeTxn,
        setEnrollments,
        setIsEdit,
        setDisableMRCEdit,
        enableContinueButton,
        setisMrcClicked,
        disableContinueButton,
        setViewAddOrChangeTxn,
        enrollmentPremiumFormContext: { ...methods },
    };

    return <EnrollmentContext.Provider value={value}>{children}</EnrollmentContext.Provider>;
};
